<template>
  <div class="articles-container">
    <h2 class="h3">
      Mean, Median and Mode Calculator
    </h2>
    <div id="heading1" class="article-section">
      <h3 class="introduction h4">
        What Are Mean, Median, and Mode?
      </h3>
      <p class="paragraph-introduction">
        Statisticians, economists, analysts, and portfolio managers prefer to use one number that measures the middle or center of data. In statistics, the <nuxt-link to="/tutorials/statistics-tutorials/mean-median-and-mode/" target="_blank">
          mean, median, and mode
        </nuxt-link> are all measures of central tendency—the latter indicating where the data is centered. The mean, median, and mode are perhaps the most widely used statistical measures because they can be applied and computed easily. Having a handy Mean, Median, and Mode Calculator will streamline your work.
      </p>
    </div>

    <div id="heading2" class="article-section">
      <h3 class="h4">
        What Is the Mean?
      </h3>
      <p class="paragraph-mean">
        The most important measure of central tendency includes the arithmetic mean (or simple average) denoted by the Greek letter µ for a <nuxt-link to="/tutorials/statistics-tutorials/population-vs-sample/" target="_blank">
          population
        </nuxt-link> and <span class="inline-formulas">\[\bar{x}\]</span> for a sample. The difference is derived from the data we use when calculating the measure. The population mean considers all data points of a given <nuxt-link to="/tutorials/statistics-tutorials/distribution-in-statistics/" target="_blank">
          distribution
        </nuxt-link>. At the same time, the sample mean reflects only a limited number of observations drawn from the population data.
      </p>
      <h4 class="h6">
        Calculating the Mean (Formula)
      </h4>
      <p class="paragraph-two-advantages">
        The equations for calculating the population and sample means are virtually the same, with a slight change in notation:
      </p>
      <p>\[\mu=\frac{\sum_{i=1}^{N}X_i}{N}\]</p>
      <p class="bottom-margin-24">
        \[\bar{X}=\frac{\sum_{i=1}^{n}X_i}{n}\]
      </p>
      <p class="bottom-margins">
        If the formula seems intimidating, you can always use the mean calculator. But it’s actually very simple when we break it down into its components.
      </p>
      <p class="bottom-margins">
        Here, n denotes the number of observations in the sample, whereas N is the number of observations in the entire population.
      </p>
      <p class="paragraph-two-mean-formula">
        We can find the mean of a data set by adding up all its components and then dividing the sum by the number of observations. For example, given the dataset 5, 2, 42, 12, 17, 23, 19, applying the formula for sample mean yields the following:
      </p>
      <p class="formulas-overflow">
        \[\bar{X}=\frac{\sum_{i=1}^{n}X_i}{n}=\frac{5+2+42+12+17+23+19}{7}=17.14\]
      </p>
      <h4 class="steps h6">
        Steps for Calculating the Mean
      </h4>
      <p class="bottom-margins">
        There are three steps for calculating the mean:
      </p>
      <ol class="bottom-margins-mean">
        <li>Add up all the values in the dataset.</li>
        <li>Count the number of observations.</li>
        <li>Divide the sum by the number of observations.</li>
      </ol>
      <p class="paragraph-mean">
        The result is the mean of the dataset.
      </p>
      <h4 class="h6">
        Advantages and Disadvantages of the Mean
      </h4>
      <p class="paragraph-one-advantages">
        The main advantage of the arithmetic mean is that it is <span class="bolded-texts">simple to understand</span> and <span class="bolded-texts">easy to calculate</span>. Moreover—compared to the other two measures of central tendency (median and mode)—the mean <span class="bolded-texts">considers all available information</span> about the size and magnitude of the observations.
      </p>
      <p class="paragraph-two-advantages">
        The <span class="bolded-texts">mean</span> is the most common measure of central tendency, but it has a notable downside—it’s easily affected by outliers. Since the measure uses all data points in a sample, it can be pulled upward or downward by abnormally large or small values. For example, suppose we estimate the mean of the following eight numbers: 1, 2, 3, 4, 5, 6, 8, and 500. By substituting the know variables, we obtain the following:
      </p>
      <p class="formulas-overflow">
        \[\bar{X}=\frac{\sum_{i=1}^{n}X_i}{n}=\frac{1+2+3+4+5+6+7+500}{8}=66\]
      </p>
      <p class="paragraph-three-advantages">
        The mean’s magnitude is considerably larger than the rest of the observations. Therefore, it is questionable how well it represents the center of data.
      </p>
    </div>

    <div id="heading3" class="article-section">
      <h3 class="h4">
        What Is the Median?
      </h3>
      <p>The median (50th Percentile) is another important measure of central tendency, representing the middle number in a data set sorted in ascending or descending order.</p>
      <img loading="lazy" alt="Middle number in an even input." src="../../../../assets/images/calculators/mean-median-mode/mmm-01-desktop.png">
      <h4 class="h6">
        Calculating the Median (Formula)
      </h4>
      <p>Finding the median depends on whether the number of observations (n) is even or odd. In an odd-numbered sample, the median is simply the middle value in an ordered data set and occupies (n + 1)/2 position:</p>
      <p class="formulas-overflow">
        \[If\ n\ is\ odd:\ Median\ (x)\ =\ x_{(n+1)/2}\]
      </p>
      <p>For example, let’s find the median for the following five numbers: 3, 5, 7, 8, and 9. The number of observations is odd and equals 5 (n=5). Therefore, the median occupies the (n + 1)/2 = 6/2 =3rd position and equals 7. The latter represents the middle number—two observations lie above this number and two below.</p>
      <img loading="lazy" alt="Median number when input is odd." src="../../../../assets/images/calculators/mean-median-mode/mmm-02-desktop.png">
      <p>There's no distinct middle number when the data sample is even numbered.  As a result, we define the median as the arithmetic average of the two middle values (in an ordered data set) that occupy the n/2 and (n + 2)/2 positions:</p>
      <p class="formulas-overflow">
        \[If\ n\ is\ even:\ Median\ (x)\ =\ \frac{x_{(n/2)}+x_{((n+2)/2)}}{2}\]
      </p>
      <p>For example, let’s calculate the median of the following data set: 3, 5, 7, 8, 9 and 12. Because this sample contains 8 observations, the median is the mean of the sorted that occupy the 6/2 = 3rd and (6+2)/2 = 4th positions. These correspond to 7 and 8, respectively. The median—as the mean of these two values—equals 7.5 or (7 + 8)/2.</p>
      <img loading="lazy" alt="Median numbers when input is even." src="../../../../assets/images/calculators/mean-median-mode/mmm-03-desktop.png">
      <p>Regardless of whether the total number of values in a data sample is odd or even, an equal number of observations lie above and below the median. In addition, a data sample can only have one median.</p>
      <h4 class="steps h6">
        Steps for Calculating the Median
      </h4>
      <p class="bottom-margin-16">
        This measure of central tendency is slightly more difficult to find than the other two. We break it down into simple steps to help you calculate it. And you can use the Mean, Median, and Mode Calculator to verify the obtained result.
      </p>
      <p class="bottom-margins">
        There are two steps for calculating the median:
      </p>
      <ol class="mean-median-mode-ol">
        <li>Sort the values into ascending or descending order.</li>
        <li>
          Determine the number of observations (n)
          <ul>
            <li>If the sample size is odd, the median is the middle value and occupies (n + 1)/2 position.</li>
            <li>If the sample size is even, the median is the average of the two middle values that occupy the n/2 and (n + 2)/2 positions.</li>
          </ul>
        </li>
      </ol>
      <h4 class="h6">
        Advantages and Disadvantages of the Median
      </h4>
      <p class="bottom-margins">
        The main advantage of the median is that—unlike the mean—it’s not skewed by extremely large or small values because the median is based on the relative positions of the data points. This makes the measure helpful when examining skewed distributions—e.g., when calculating the ordinary income in a country because it’s not affected by a small number of extremely high or low-income outlier households.
      </p>
      <p>Let’s look at another example. Suppose we estimate the median of these seven numbers: 2, 3, 4, 5, 6, 7, and 1,000. The number of observations is odd and equals 7 (n=7). Therefore, the median occupies the (n + 1)/2 = 8/2 =4th position and equals 5. Note that in this particular data set, the presence of an outlier (1,000) does not affect the median. Even if the eight observations were 10, the result would be unchanged. But if we compute the arithmetic mean, the result would be 146.7.</p>
      <p class="formulas-overflow formulas-top-down">
        \[\bar{X}=\frac{\sum_{i=1}^{n}X_i}{n}=\frac{2+3+4+5+6+7+100}{7}=146.7\]
      </p>
      <p class="bottom-margins">
        Clearly, this value does not provide a good representation of the other six observations in the data set.
      </p>
      <p class="bottom-margins">
        But the median also has its disadvantages. One is that the measure does not consider all available information about the size and magnitude of the observations and focuses only on their relative position. For example, the median of 20, 50, 100, 120, and 130 is 100. We can replace observations 20 and 50 with any values smaller than 100 and observations 120 and 130 with any two values greater than 100 without affecting the median value. (The median is insensitive to any values around the middle observation.)
      </p>
      <p>Obtaining the median is also more computationally complex. First, we need to sort the numbers in ascending or descending order, determine whether the sample size is odd or even, and apply one of two calculations. Using a median calculator eliminates this issue.</p>
    </div>

    <div id="heading4" class="article-section">
      <h3 class="h4">
        What Is the Mode?
      </h3>
      <p class="paragraph-one-advantages">
        The mode is the third important measure of central tendency—the value that occurs most often in a distribution. For example, the mode in the following data set [1, 4, 9, 5, 5, 5, 9, 3] is 5 because it occurs most often. Such a distribution is <span class="bolded-texts">unimodal</span> because it has one value that occurs most often.
      </p>
      <p class="paragraph-median-advantages">
        A distribution can have more than one mode or even no mode. For example, the mode in [2, 2, 3, 5, 5] equals 2 and 5 since both values appear twice each. In such a case, the dataset is <span class="bolded-texts">bimodal</span> because it has two modes.
      </p>
      <p class="paragraph-median-advantages">
        Generally, a data set with more than two modes is <span class="bolded-texts">multimodal</span>. Now let’s examine the following dataset [1,2,3,4,5]. All the values in the data set are different, so no mode is available. In such cases, the mean or median are better alternatives.
      </p>
      <h4 class="h6">
        Calculating the Mode
      </h4>
      <p class="bottom-margins">
        In large datasets, using a calculator to find the mode will save you valuable time. But this is the easiest measure of central tendency to find manually—simply detect the most frequent value in a set.
      </p>
      <p>
        Sometimes when we explore continuous distributions [0.852…,0.862…,0.872….], we may not have a modal outcome since there are no two values that will be precisely the same. A common practice in such cases is to group data into intervals and explore which has the highest frequency—a practice in statistics known as finding the <span class="bolded-texts">modal interval</span>.
      </p>
      <h4 class="steps h6">
        Steps for Calculating the Mode
      </h4>
      <p class="bottom-margins">
        There are three steps for calculating the mode:
      </p>
      <ol class="mean-median-mode-ol">
        <li>Place all numbers into ascending or descending order</li>
        <li>Count how many times each value appears in the set</li>
        <li>Check the one that appears the most. (This number is the mode.)</li>
      </ol>
      <h4 class="h6">
        Advantages and Disadvantages of the Mode
      </h4>
      <p class="bottom-margins">
        The main advantage of the mode is that it’s not affected by extremely large or small values. Another advantage is that the measure is easy to understand and calculate. Moreover—unlike the mean and median—it can be used for both quantitative and qualitative data.
      </p>
      <p>But the measure also has disadvantages. As mentioned previously, there are cases when it’s undefined because there are no repeats in the distribution. In addition, the mode is not based on all the values in the dataset.</p>
    </div>

    <div id="heading5" class="article-section">
      <h3 class="h4">
        Comparison of Mean, Median, and Mode
      </h3>
      <h4 class="h6">
        Why Is Skewness Important?
      </h4>
      <p class="bottom-margins">
        <nuxt-link to="/tutorials/statistics-tutorials/skewness-example/" target="_blank">
          Skewness
        </nuxt-link> tells us where the data is situated.
      </p>
      <p class="paragraph-mean">
        The <span class="bolded-texts">mean</span>, <span class="bolded-texts">median</span> and <span class="bolded-texts">mode</span> should be used together to understand the dataset better. <a class="measure-asymmetry-a" href="https://www.wisdomjobs.com/e-university/research-methodology-tutorial-355/measures-of-asymmetry-skewness-11503.html" target="_blank">Measures of asymmetry</a>, like <nuxt-link to="/calculators/skewness-calculator" target="_blank">
          skewness
        </nuxt-link>, are the link between central tendency measures and probability theory, which ultimately allows us to understand the data we’re working with.
      </p>
      <h4 class="h6">
        When Will the Mean, Median, and Mode Be Equal?
      </h4>
      <p>The mean, median, and mode are equal when the distribution is perfectly symmetrical and non-skewed. As the distribution becomes more skewed, the difference between the three measures different becomes larger.</p>
      <img loading="lazy" class="bigger-img" alt="Median numbers when input is even." src="../../../../assets/images/calculators/mean-median-mode/mmm-04-desktop.png">
      <p>When we have right-skewed distribution, the mean is larger than the median, and the mode is the value with the highest visual representation.</p>
      <img loading="lazy" class="bigger-img" alt="Positively Skewed graph." src="../../../../assets/images/calculators/mean-median-mode/mmm-05-desktop.png">
      <img loading="lazy" class="bigger-img" alt="Negative Skewed graph." src="../../../../assets/images/calculators/mean-median-mode/mmm-06-desktop.png">
      <p class="bottom-margin-40">
        In a negatively skewed frequency distribution, the mean is lower than the median, and once again, the mode defines the highest point. Such a distribution is also called left skewed because the outliers are to the left.
      </p>
      <h4 class="h6">
        Which Measure of Central Tendency is the Best?
      </h4>
      <p class="bottom-margins-mean">
        The answer is simple. There is no best, but using only one is the worst option. In other words, the three main measures are best used in combination with each other because they have complementary strengths and weaknesses.
      </p>
      <p class="paragraph-mean">
        The Mean, Median, and Mode Calculator generates all three measures of central tendency simultaneously, streamlining your work.
      </p>
      <h4 class="h6">
        How to Find the Mean, Median, and Mode of a Dataset: Example
      </h4>
      <p class="bottom-margins-mean">
        What is the mean, median mode of 1, 2, 3, 4, 5?
      </p>
      <p class="paragraph-introduction">
        We calculate the mean by adding up all values in the dataset and dividing them by the number of observations.
      </p>
      <p class="formulas-overflow paragraph-introduction">
        \[\bar{X}=\frac{\sum_{i=1}^{n}X_i}{n}=\frac{1+2+3+4+5}{5}=3\]
      </p>
      <p class="bottom-margins-mean">
        Based on this, we find that the mean of the dataset is 3.
      </p>
      <p class="paragraph-introduction">
        To obtain the median, we organize the data set in ascending order, then find the middle value that separates the dataset in half. Here, we skip the first step since the values are consecutive. In our case, the median equals 3 and occupies the (n+1)/2 position (or the third position in the dataset).
      </p>
      <img loading="lazy" class="last-image-mmm" alt="Result's position in the dataset." src="../../../../assets/images/calculators/mean-median-mode/mmm-07-desktop.png">
      <p>
        The mode is the value that appears most frequently in a set of values. In this case, there is no mode because all values appear only once in the dataset.
      </p>
    </div>
  </div>
</template>

<script>
export default {
  mounted() {
    this.renderMathJax();
  },
  methods: {
    renderMathJax() {
      if (window.MathJax) {
        window.MathJax.Hub.Config({
          tex2jax: {
            inlineMath: [['$', '$'], ['\\(', '\\)']],
            processEscapes: true,
          },
          'HTML-CSS': {
            linebreaks: { automatic: true, width: 'container' },
          },
          SVG: {
            linebreaks: { width: '10000px' },
          },
          jax: ['input/TeX', 'output/SVG'],
        });
        window.MathJax.Hub.Queue(['Typeset', window.MathJax.Hub]);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.articles-container {
  .article-section {
    p {
      margin: 0;
    }

    .bottom-margin-16 {
      margin-bottom: var(--spacing-16);
    }

    .measure-asymmetry-a {
      display: unset;
    }

    img {
      &.bigger-img {
        &:nth-last-of-type(2) {
          margin: 0 0 var(--spacing-40);
        }
      }

      &.last-image-mmm {
        margin: var(--spacing-16) 0;
      }
    }

    .introduction.h4 {
      margin-top: 0;
    }

    .mozilla-adjustment {
      font-weight: bold;
      font-family: Arial, sans-serif;
    }

    .paragraph-introduction {
      margin: 0;
    }

    .paragraph-mean {
      margin: 0 0 var(--spacing-40);
    }

    .bottom-margins-mean {
      margin: 0 0 var(--spacing-20);
    }

    .steps {
      margin-top: var(--spacing-24);
    }

    .paragraph-one-mean-formula {
      margin: var(--spacing-24) 0 var(--spacing-4);
    }

    .paragraph-two-mean-formula {
      margin-bottom: var(--spacing-24);
    }

    .paragraph-one-advantages {
      margin: 0 0 var(--spacing-20);
    }

    .paragraph-two-advantages {
      margin: 0 0 var(--spacing-24);
    }

    .paragraph-three-advantages {
      margin: var(--spacing-24) 0 0;
    }

    .paragraph-median-advantages {
      margin-bottom: var(--spacing-20);
    }

    ul {
      list-style-type: disc;
    }

    .calc-steps-ol {
      margin-bottom: var(--spacing-20);

      li {
        margin: 0;
      }
    }

    .mean-median-mode-ol {
      margin-bottom: var(--spacing-40);
      line-height: 150%;

      ul {
        padding-left: 4px;
        list-style-position: outside;
      }

      li {
        margin: 0;
      }
    }
  }
}

@include bp($bp-mobile) {
  .row {
    .articles-container {
      .article-section {
        img {
          margin: var(--spacing-32) 0;

          &.bigger-img {
            &:nth-of-type(3) {
              margin: 0 0 var(--spacing-32);
            }
          }
        }

        .paragraph-mean {
          margin: 0 0 var(--spacing-32);
        }
      }
    }
  }
}

@include bp($bp-phone) {
  .row {
    .articles-container {
      .article-section {
        ul {
          padding-left: 0;
        }

        .calc-steps-ol,
        .mean-median-mode-ol {
          padding-left: var(--spacing-20);
        }
      }
    }
  }
}

@import '../../../../assets/sass/ds/calculators/articles.scss';
</style>
